import { useDocumentsFilter } from '@features/documents/contexts/documents-filter/useDocumentsFilter';
import { useDocuments } from '@features/documents/hooks/use-documents/useDocuments';
import { ContentLoader } from '@shared/components/content-loader/ContentLoader';
import { InlineFeedback } from '@shared/components/inline-feedback/InlineFeedback';
import * as React from 'react';

import type { DocumentsContentProps } from './DocumentsContent.types';

import { DocumentsBreadcrumb } from './documents-breadcrumb/DocumentsBreadcrumb';
import { DocumentsTable } from './documents-table/DocumentsTable';
import styles from './DocumentsContent.module.css';

const DocumentsContent = React.memo(
  ({
    documentType,
    organizationId,
    canDownload,
    rootLabel,
  }: Readonly<DocumentsContentProps>): React.ReactElement => {
    const { filters, isFiltersApplied, navigateToRoot } = useDocumentsFilter();

    React.useEffect(() => {
      navigateToRoot();
    }, [documentType, navigateToRoot]);

    const { data, isLoading, isError } = useDocuments({
      documentType,
      organizationId,
      folderId: filters.folderId ?? undefined,
      searchText: filters.searchText || undefined,
    });

    if (isLoading) {
      return <ContentLoader />;
    }

    if (isError) {
      return <InlineFeedback type="data-error" />;
    }

    if (!data || data.totalCount === 0) {
      if (isFiltersApplied) {
        return <InlineFeedback type="no-search-results" />;
      }
      return <InlineFeedback type="so-empty" />;
    }

    return (
      <div data-testid="documents-content" className={styles.documentsContent}>
        {!isFiltersApplied && <DocumentsBreadcrumb rootLabel={rootLabel} />}
        <DocumentsTable
          documents={data.documents}
          organizationId={organizationId}
          canDownload={canDownload}
        />
      </div>
    );
  }
);

DocumentsContent.displayName = 'DocumentsContent';

export { DocumentsContent };
